import React, { useState, useEffect, useRef } from "react";
import {
  jaanoBubbleIntegration,
  jaanoWhatsappIntegration,
  jaanoFramerIntegration,
} from "./data";
import MainLayout from "../../layouts/MainLayOut";
import { LuText } from "react-icons/lu";
import { HiMenu } from "react-icons/hi";

const StepImages = ({ images }) => {
  const [fullscreenImage, setFullscreenImage] = useState(null);

  return (
    <>
      <div className="mt-4 grid grid-cols-1 gap-4">
        {images?.map((src, index) => (
          <img
            key={index}
            src={src}
            alt={`Step ${index + 1}`}
            onClick={() => setFullscreenImage(src)}
            className="rounded border shadow max-h-[400px] cursor-pointer hover:scale-105 transition-transform"
          />
        ))}
      </div>

      {fullscreenImage && (
        <div
          onClick={() => setFullscreenImage(null)}
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center cursor-zoom-out">
          <img
            src={fullscreenImage}
            alt="Fullscreen"
            className="max-w-full max-h-full rounded-lg shadow-xl transition-transform scale-100"
          />
        </div>
      )}
    </>
  );
};
export default function Doc() {
  const integrations = {
    Bubble: jaanoBubbleIntegration,
    Framer: jaanoFramerIntegration,
    WhatsApp: jaanoWhatsappIntegration,
  };

  const [activePlatform, setActivePlatform] = useState("Bubble");
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const stepRefs = useRef([]);
  const data = integrations[activePlatform];

  // IntersectionObserver to update active TOC entry
  useEffect(() => {
    if (!data?.steps?.length) return;

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1, // improved accuracy for short sections
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = stepRefs.current.findIndex((el) => el === entry.target);
          if (index !== -1) setActiveStepIndex(index);
        }
      });
    }, observerOptions);

    data.steps.forEach((_, idx) => {
      const el = stepRefs.current[idx];
      if (el) observer.observe(el);
    });

    return () => {
      data.steps.forEach((_, idx) => {
        const el = stepRefs?.current[idx];
        if (el) observer.unobserve(el);
      });
    };
  }, [data]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <MainLayout>
      <div className="flex flex-col md:flex-row min-h-screen relative break-all">
        <div className="md:hidden flex gap-2 items-center  bg-gray-50 px-4 pt-4">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="bg-darkprimary p-2 rounded shadow"
            aria-label="Open menu">
            <HiMenu size={20} className="fill-white" />
          </button>
          <span>
            Integration {">"} {activePlatform}
          </span>
        </div>

        {/* Mobile backdrop */}
        <div
          className={`fixed inset-0 z-40 bg-black bg-opacity-30 transition-opacity duration-300 ${
            mobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
          onClick={() => setMobileMenuOpen(false)}></div>

        {/* Mobile nav */}
        <aside
          className={`fixed top-0 left-0 h-full w-64 bg-white z-50 p-6 shadow-lg transform transition-transform duration-300 md:hidden ${
            mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="absolute top-4 right-4 text-gray-600"
            aria-label="Close menu">
            ✕
          </button>
          <h2 className="text-base font-bold mb-2">Integrations</h2>
          <ul className="space-y-1">
            {Object.keys(integrations).map((platform) => (
              <li
                key={platform}
                onClick={() => {
                  setActivePlatform(platform);
                  setActiveStepIndex(0);
                  setMobileMenuOpen(false);
                }}
                className={`cursor-pointer px-3 py-2 rounded hover:bg-blue-100 ${
                  platform === activePlatform
                    ? "bg-blue-200 text-blue-800 font-semibold"
                    : ""
                }`}>
                {platform}
              </li>
            ))}
          </ul>
        </aside>
        {/* Navigation - Left */}
        <nav className="hidden md:block sticky top-0 h-screen w-1/5 border-r bg-gray-50 p-6 overflow-y-auto">
          <h2 className="text-base font-bold mb-2">Integrations</h2>
          <ul className="space-y-1">
            {Object.keys(integrations).map((platform) => (
              <li
                key={platform}
                onClick={() => {
                  setActivePlatform(platform);
                  setActiveStepIndex(0);
                }}
                className={`cursor-pointer px-3 py-2 rounded hover:bg-blue-100 ${
                  platform === activePlatform
                    ? "bg-blue-200 text-blue-800 font-semibold"
                    : ""
                }`}>
                {platform}
              </li>
            ))}
          </ul>
        </nav>

        {/* Main Content - Center */}
        <main className="flex flex-1 gap-3 p-4 overflow-y-auto bg-gray-50 scrollbar-hide">
          <div className="flex flex-col w-full md:w-2/3 p-2 md:p-8">
            <div className="flex flex-col">
              <label className="text-sm">Integration</label>
              <h1 className="text-2xl font-bold">{data.platform}</h1>
            </div>

            {data?.desc && <p className="text-sm my-2">{data.desc}</p>}

            {data.warning && (
              <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-6 text-sm rounded">
                <p className="font-semibold">Important:</p>
                <p>{data.warning}</p>
              </div>
            )}

            {data?.prerequisites?.length > 0 && (
              <>
                <h6 className="text-lg font-semibold mb-4">Before we start</h6>
                <ol className="space-y-6 text-base">
                  {data.prerequisites.map((pre, index) => (
                    <li key={index} className="ml-5">
                      <label className="mb-2 font-semibold block">
                        {pre.title}
                      </label>
                      <ul className="list-disc list-inside space-y-1 ml-5">
                        {pre.steps?.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ul>
                      {pre.note && (
                        <p className="text-sm text-gray-700 ml-5 italic">
                          {pre.note}
                        </p>
                      )}
                    </li>
                  ))}
                </ol>
              </>
            )}

            <p className="text-lg my-3">
              Follow this step-by-step guide to integrate your{" "}
              <strong>{data.chatbot}</strong> chatbot into{" "}
              <strong>{data.platform}</strong>.
            </p>

            {data.steps?.map((step, idx) => (
              <section
                ref={(el) => (stepRefs.current[idx] = el)}
                key={idx}
                id={`step-${idx}`}
                className="mb-10 scroll-mt-24">
                <h2 className="text-lg font-semibold mb-2">{step.title}</h2>
                <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-5">
                  {step.instructions.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ol>
                <StepImages images={step.images} />
              </section>
            ))}

            {data.metaUsage && (
              <div className="bg-blue-50 border border-blue-200 p-4 mb-6 text-sm rounded">
                <p className="font-semibold">{data.metaUsage.note}</p>
                <p>{data.metaUsage.tip}</p>
              </div>
            )}

            {data.congratulations && (
              <div className="bg-green-100 border border-green-300 text-green-900 p-4 mb-6 rounded">
                {data.congratulations}
              </div>
            )}

            <div className="min-h-10 w-full" />
          </div>

          {/* TOC - Right */}
          <aside className="hidden md:block sticky top-0 h-screen w-1/3 p-6 overflow-y-auto scrollbar-hide">
            <h3 className="text-sm font-semibold mb-4 flex gap-1 items-center">
              <LuText size={15} /> On this page
            </h3>
            <ol className="list-none space-y-2 text-sm">
              {data.steps?.map((step, idx) => (
                <li key={idx}>
                  <button
                    onClick={() =>
                      document
                        .getElementById(`step-${idx}`)
                        ?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                    className={`text-left w-full px-1 py-1 rounded transition hover:text-blue-800 hover:underline ${
                      idx === activeStepIndex
                        ? "font-semibold text-blue-800"
                        : "text-blue-600"
                    }`}>
                    {step.title}
                  </button>
                </li>
              ))}
            </ol>
          </aside>
        </main>
      </div>
    </MainLayout>
  );
}
